import { useCallback } from 'react';
import { useInstallPrompt } from '../../../hooks/useInstallPrompt';
import printCss from './competitionPrint.css?raw';

/**
 * Returns a trigger that prints the hidden tournament sheets.
 *
 * On every platform except an installed iOS PWA, `window.print()` opens the
 * print dialog directly (the `@media print` rules then reveal only the sheets).
 *
 * iOS standalone web apps (added to the Home Screen) expose no print/share UI,
 * so `window.print()` there is a silent no-op. For that case we open a
 * self-contained copy of the already-rendered sheets in Safari, where the user
 * can print or "Salvar em PDF" from the share sheet.
 */
export function usePrintSheets() {
  const { isIOS, isInstalled } = useInstallPrompt();

  return useCallback(() => {
    const node = document.querySelector('.tournament-print');
    if (isIOS && isInstalled && node) {
      const win = openSheetsInSafari(node.outerHTML);
      if (!win) {
        window.alert(
          'Para imprimir no iPhone/iPad, abra este torneio no Safari e use Compartilhar → Imprimir ou Salvar em PDF.'
        );
      }
      return;
    }
    window.print();
  }, [isIOS, isInstalled]);
}

/**
 * Opens the sheets as a standalone HTML document in a new tab. The document
 * inlines the print stylesheet plus a screen view (A4 "paper" + a print bar),
 * so it renders and prints on its own with no app/session dependency.
 */
function openSheetsInSafari(sheetsHtml: string): Window | null {
  const doc = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Fichas do torneio</title>
<style>
${printCss}
/* On-screen view of the standalone window: A4 "paper" + a print bar. */
body { margin: 0; background: #d9d7cf; }
.tournament-print { display: block; }
.tournament-print .sheet {
  width: 210mm; min-height: 297mm; margin: 0 auto 16px; padding: 12mm;
  background: #fff; box-shadow: 0 2px 14px rgba(0, 0, 0, 0.22);
}
.print-bar {
  position: sticky; top: 0; z-index: 10; display: flex; align-items: center;
  justify-content: space-between; gap: 12px; padding: 12px 16px;
  background: #0a1128; color: #fff; font: 600 14px/1.3 system-ui, -apple-system, sans-serif;
}
.print-bar button {
  appearance: none; border: 0; border-radius: 8px; padding: 8px 14px;
  background: #2dd4bf; color: #04201c; font: inherit; font-weight: 700;
}
@media print {
  .print-bar { display: none !important; }
  body { background: #fff; }
  .tournament-print .sheet { width: auto; min-height: 0; margin: 0; padding: 0; box-shadow: none; }
}
</style>
</head>
<body>
<div class="print-bar">
  <span>Toque em Imprimir, ou use Compartilhar → Imprimir / Salvar em PDF</span>
  <button type="button" onclick="window.print()">Imprimir</button>
</div>
${sheetsHtml}
</body>
</html>`;

  const url = URL.createObjectURL(new Blob([doc], { type: 'text/html' }));
  const win = window.open(url, '_blank');
  // Release the object URL once Safari has had time to load it.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return win;
}
